import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
    Tabs, Upload, Button, Progress, message,
    Table, Tag, Tooltip, Radio, InputNumber, Checkbox, Dropdown
} from 'antd'
import {
    InboxOutlined, CloudUploadOutlined, FileTextOutlined,
    DownloadOutlined, BarChartOutlined, CloudOutlined,
    SettingOutlined, FilterOutlined, CloseCircleOutlined, DownOutlined
} from '@ant-design/icons'
import * as echarts from 'echarts'
import { analyzeFile, analyzeText, subscribeStream, generateWordCloud, subscribeGenerateStream } from '../../api/wordcloud'
import LoginModal from '../../components/LoginModal'
import WordcloudVipModal from '../../components/WordcloudVipModal'
import { setVipRequiredHandler } from '../../utils/errorHandler'
import styles from './index.module.css'

const { Dragger } = Upload
const { TextArea } = require('antd').Input

const POS_MAP = {
    n: '普通名词', f: '方位名词', s: '处所名词', t: '时间名词',
    nr: '人名', ns: '地名', nt: '机构团体名', nw: '作品名',
    nz: '其他专名', v: '普通动词', vd: '动副词', vn: '名动词',
    a: '形容词', ad: '副形词', an: '名形词', d: '副词',
    m: '数量词', q: '量词', r: '代词', p: '介词',
    c: '连词', u: '助词', xc: '其他虚词', w: '标点符号',
    PER: '人名', LOC: '地名', ORG: '机构名', TIME: '时间',
}

const COLORS = [
    '#1677ff', '#13c2c2', '#52c41a', '#fa8c16', '#eb2f96',
    '#722ed1', '#2f54eb', '#08979c', '#389e0d', '#d46b08',
    '#c41d7f', '#531dab', '#096dd9', '#006d75', '#237804',
    '#ad4e00', '#9e1068', '#391085',
]


const SHAPE_OPTIONS = [
    { value: 'circle',    label: '圆形',  svg: <ellipse cx="12" cy="12" rx="10" ry="10" /> },
    { value: 'diamond',   label: '菱形',  svg: <polygon points="12,2 22,12 12,22 2,12" /> },
    { value: 'square',    label: '正方形', svg: <rect x="2" y="2" width="20" height="20" /> },
    { value: 'rectangle', label: '长方形', svg: <rect x="1" y="5" width="22" height="14" /> },
    { value: 'triangle',  label: '三角形', svg: <polygon points="12,2 22,22 2,22" /> },
    { value: 'pentagon',  label: '五边形', svg: <polygon points="12,2 22,9 18,21 6,21 2,9" /> },
    { value: 'star',      label: '五角星', svg: <polygon points="12,2 14.9,9.5 22.5,9.5 16.5,14.5 18.8,22 12,17.5 5.2,22 7.5,14.5 1.5,9.5 9.1,9.5" /> },
    { value: 'heart',     label: '爱心',  svg: <path d="M12,21 C12,21 3,14 3,8.5 C3,5.4 5.4,3 8.5,3 C10.2,3 11.7,3.9 12,5 C12.3,3.9 13.8,3 15.5,3 C18.6,3 21,5.4 21,8.5 C21,14 12,21 12,21 Z" /> },
]

const WordCloudPage = () => {
    const [inputMode, setInputMode] = useState('file')
    const [fileList, setFileList] = useState([])
    const [textContent, setTextContent] = useState('')

    const [cloudShape, setCloudShape] = useState('circle')
    const [topN, setTopN] = useState(null)
    const [wcImageUrl, setWcImageUrl] = useState('')
    const [generatingCloud, setGeneratingCloud] = useState(false)
    const [cloudProgress, setCloudProgress] = useState({ current: 0, total: 0, message: '' })

    const [posFilter, setPosFilter] = useState([])
    const [analyzing, setAnalyzing] = useState(false)
    const [analysisPhase, setAnalysisPhase] = useState(null) // 'segmenting' | 'generating'
    const [progress, setProgress] = useState({ current: 0, total: 0, message: '' })
    const [wordData, setWordData] = useState([])
    const [chartTab, setChartTab] = useState('cloud')
    const [loginModalOpen, setLoginModalOpen] = useState(false)
    const [vipModalOpen, setVipModalOpen] = useState(false)

    const barRef = useRef(null)
    const barInstance = useRef(null)

    const [messageApi, contextHolder] = message.useMessage()

    const isLoggedIn = !!localStorage.getItem('token')

    // 设置需要开通会员（err_code 8012）处理器
    useEffect(() => {
        const handler = () => setVipModalOpen(true)
        setVipRequiredHandler(handler)
        return () => setVipRequiredHandler(null)
    }, [])

    const handleVipSuccess = useCallback(() => {
        setVipModalOpen(false)
    }, [])

    const renderBar = useCallback((data) => {
        if (!barRef.current) return
        if (barInstance.current && barInstance.current.getDom() !== barRef.current) {
            barInstance.current.dispose()
            barInstance.current = null
        }
        const view50   = data.slice(0, 50)
        const words    = view50.map(([w]) => w)
        const counts   = view50.map(([, c]) => c)
        const maxCount = counts[0] || 1
        // Each bar needs ~28px vertical space; total = bars * 28 + 60 axis padding
        barRef.current.style.height = (view50.length * 17 + 60) + 'px'
        if (!barInstance.current) barInstance.current = echarts.init(barRef.current)
        barInstance.current.resize()
        barInstance.current.setOption({
            tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
            grid: { left: 8, right: 8, bottom: 24, top: 24, containLabel: true },
            xAxis: {
                type: 'value',
                max: Math.ceil(maxCount * 1.20),
                axisLabel: { color: '#555' },
                splitLine: { lineStyle: { color: '#f0f0f0' } },
            },
            yAxis: {
                type: 'category',
                // inverse:true keeps data[0] (highest) at top; ECharts allocates correct margin for it
                inverse: true,
                data: words,
                axisLabel: { color: '#333', fontSize: 12, formatter: (v) => v.length > 8 ? v.slice(0, 8) + '…' : v },
                axisLine: { lineStyle: { color: '#ddd' } },
            },
            series: [{
                type: 'bar',
                data: counts,
                clip: false,
                barMaxWidth: 12,
                itemStyle: { color: (p) => COLORS[p.dataIndex % COLORS.length], borderRadius: [0, 4, 4, 0] },
                label: { show: true, position: 'right', color: '#555', fontSize: 11 },
            }],
        }, true)
    }, [])


    // force 力导向布局：参考 HTML 示例，对数尺寸 + 真实共现边 + 物理斥力

    useEffect(() => {
        if (wordData.length === 0) return
        const data = topN > 0 ? wordData.slice(0, topN) : wordData
        if (chartTab === 'bar') {
            setTimeout(() => renderBar(data), 50)
        }
    }, [chartTab, wordData, topN, renderBar])

    useEffect(() => {
        const onResize = () => { barInstance.current?.resize() }
        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [])

    const doAnalyze = async () => {
        if (inputMode === 'file' && fileList.length === 0) return messageApi.warning('请先选择要分析的文件')
        if (inputMode === 'text' && !textContent.trim()) return messageApi.warning('请输入要分析的文本内容')

        setAnalyzing(true)
        setAnalysisPhase('segmenting')
        setWordData([])
        setWcImageUrl('')
        setGeneratingCloud(false)
        setProgress({ current: 0, total: 0, message: '正在提交任务...' })
        setCloudProgress({ current: 0, total: 0, message: '' })

        try {
            let taskData
            if (inputMode === 'file') {
                taskData = await analyzeFile(fileList[0])
            } else {
                taskData = await analyzeText(textContent)
            }
            const { task_id } = taskData
            setProgress((p) => ({ ...p, message: '任务已创建，开始分析' }))
            await subscribeStream(
                task_id,
                (prog) => setProgress({ current: prog.current, total: prog.total, message: prog.message }),
                async (result) => {
                    const defaultN = result.length <= 400 ? result.length : 400
                    setTopN(defaultN)
                    setWordData(result)
                    setPosFilter([])
                    messageApi.success(`分词完成，共提取 ${result.length} 个关键词`)
                    // Switch to cloud generation phase (70-100%)
                    setAnalysisPhase('generating')
                    setCloudProgress({ current: 0, total: 0, message: '正在提交词云生成任务...' })
                    setChartTab('cloud')
                    // 启动词云生成
                    try {
                        const filtered = defaultN > 0 ? result.slice(0, defaultN) : result
                        const cloudTaskData = await generateWordCloud({ data: filtered, shape: cloudShape })
                        const { task_id: cloudTaskId } = cloudTaskData
                        setCloudProgress((p) => ({ ...p, message: '词云渲染中...' }))
                        await subscribeGenerateStream(
                            cloudTaskId,
                            (prog) => setCloudProgress({ current: prog.current, total: prog.total, message: prog.message }),
                            (cloudResult) => {
                                setWcImageUrl(cloudResult.image || '')
                                setAnalyzing(false)
                                setAnalysisPhase(null)
                            },
                            (err) => { setAnalyzing(false); setAnalysisPhase(null); if (!err?.handled) messageApi.error(err.message || '词云生成失败') },
                        )
                    } catch (err) {
                        setAnalyzing(false)
                        setAnalysisPhase(null)
                        if (!err?.handled) messageApi.error(err.message || '词云生成请求失败')
                    }
                },
                (err) => { setAnalyzing(false); setAnalysisPhase(null); if (!err?.handled) messageApi.error(err.message || '分析失败') },
            )
        } catch (err) {
            setAnalyzing(false)
            setAnalysisPhase(null)
            if (!err?.handled) messageApi.error(err.message || '请求失败，请稍后重试')
        }
    }

    const handleAnalyze = async () => {
        if (!isLoggedIn) {
            setLoginModalOpen(true)
            return
        }
        doAnalyze()
    }

    const handleGenerateCloud = async (data, shape, n) => {
        if (!data || data.length === 0) return
        setGeneratingCloud(true)
        setWcImageUrl('')
        setChartTab('cloud')
        setCloudProgress({ current: 0, total: 0, message: '正在提交词云生成任务...' })
        try {
            const filtered = n > 0 ? data.slice(0, n) : data
            const taskData = await generateWordCloud({ data: filtered, shape })
            const { task_id } = taskData
            setCloudProgress((p) => ({ ...p, message: '词云渲染中...' }))
            await subscribeGenerateStream(
                task_id,
                (prog) => setCloudProgress({ current: prog.current, total: prog.total, message: prog.message }),
                (result) => {
                    setWcImageUrl(result.image || '')
                    setGeneratingCloud(false)
                },
                (err) => { setGeneratingCloud(false); if (!err?.handled) messageApi.error(err.message || '词云生成失败') },
            )
        } catch (err) {
            setGeneratingCloud(false)
            if (!err?.handled) messageApi.error(err.message || '词云生成请求失败')
        }
    }

    const downloadBarChart = () => {
        if (!barInstance.current) return messageApi.warning('图表尚未渲染')
        const url = barInstance.current.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' })
        const a = document.createElement('a'); a.href = url; a.download = 'wordfreq_bar.png'; a.click()
    }

    const downloadWcImage = () => {
        if (!wcImageUrl) return messageApi.warning('词云图尚未生成')
        const a = document.createElement('a'); a.href = wcImageUrl; a.download = 'wordcloud.png'; a.click()
    }

    const downloadCSV = () => {
        if (wordData.length === 0) return
        const csvContent = `\uFEFFword,count\n${wordData.map(([w, c]) => `"${w}",${c}`).join('\n')}`
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a'); a.href = url; a.download = 'wordcloud_data.csv'; a.click()
        URL.revokeObjectURL(url)
    }

    const uploadProps = {
        name: 'file', multiple: false, accept: '.docx,.txt', maxCount: 1, fileList, showUploadList: false,
        beforeUpload: (file) => {
            const ext = file.name.split('.').pop().toLowerCase()
            if (!['docx', 'txt'].includes(ext)) { messageApi.error('仅支持 DOCX、TXT 格式'); return Upload.LIST_IGNORE }
            if (file.size > 10 * 1024 * 1024) { messageApi.error('文件大小不能超过 10MB'); return Upload.LIST_IGNORE }
            setFileList([file]); return false
        },
        onRemove: () => setFileList([]),
    }

    const displayData = topN > 0 ? wordData.slice(0, topN) : wordData

    const availablePos = useMemo(() => {
        const seen = new Set()
        const result = []
        for (const item of wordData) {
            const pos = item[2]
            if (pos && !seen.has(pos)) { seen.add(pos); result.push(pos) }
        }
        return result
    }, [wordData])

    const columns = [
        { title: '排名', key: 'rank', width: 70, render: (_, __, idx) => <Tag color={idx < 3 ? ['gold', 'silver', '#cd7f32'][idx] : 'default'}>{idx + 1}</Tag> },
        { title: '词语', dataIndex: 0, key: 'word', render: (w) => <b>{w}</b> },
        {
            title: '频次', dataIndex: 1, key: 'count',
            render: (c, row) => {
                const word = row[0] || ''
                const colorIdx = Math.abs(word.split('').reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) | 0, 0)) % COLORS.length
                return (
                    <Tooltip title={`出现 ${c} 次`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ height: 12, width: `${Math.round((c / (wordData[0]?.[1] || 1)) * 160)}px`, background: COLORS[colorIdx], borderRadius: 4, minWidth: 4 }} />
                            <span style={{ color: '#555', fontSize: 13 }}>{c}</span>
                        </div>
                    </Tooltip>
                )
            },
        },
        {
            title: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, userSelect: 'none' }}>
                    词性
                    <Dropdown
                        trigger={['click']}
                        dropdownRender={() => (
                            <div style={{ padding: '8px 4px', display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120, background: '#fff', borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,0.10)' }}>
                                <div
                                    style={{ padding: '4px 8px', cursor: 'pointer', fontWeight: posFilter.length === 0 ? 600 : 400, color: posFilter.length === 0 ? '#1677ff' : 'inherit', borderRadius: 4, background: posFilter.length === 0 ? '#e6f4ff' : 'transparent' }}
                                    onClick={() => setPosFilter([])}
                                >全部</div>
                                {availablePos.map(pos => (
                                    <div key={pos} style={{ padding: '4px 8px', borderRadius: 4, background: posFilter.includes(pos) ? '#e6f4ff' : 'transparent' }}>
                                        <Checkbox
                                            checked={posFilter.includes(pos)}
                                            onChange={(e) => {
                                                const next = e.target.checked
                                                    ? [...posFilter, pos]
                                                    : posFilter.filter(k => k !== pos)
                                                setPosFilter(next)
                                            }}
                                        >{POS_MAP[pos] || pos}</Checkbox>
                                    </div>
                                ))}
                            </div>
                        )}
                    >
                        <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: '1px 5px', borderRadius: 4,
                            background: posFilter.length ? '#1677ff' : '#e6f4ff',
                            border: `1px solid ${posFilter.length ? '#1677ff' : '#91caff'}`,
                            cursor: 'pointer', transition: 'background 0.2s, border-color 0.2s',
                        }}>
                            <DownOutlined style={{ fontSize: 9, color: posFilter.length ? '#fff' : '#1677ff' }} />
                        </span>
                    </Dropdown>
                </span>
            ),
            dataIndex: 2, key: 'pos', width: 100,
            render: (pos) => pos ? (POS_MAP[pos] || pos) : <span style={{ color: '#bbb' }}>-</span>,
        },
    ]

    // Unified progress for full-analysis flow (0-70% segmenting, 70-100% generating)
    const tableData = posFilter.length > 0 ? displayData.filter(item => posFilter.includes(item[2])) : displayData

    const unifiedPercent = (() => {
        if (analysisPhase === 'segmenting') {
            return progress.total > 0 ? Math.round((progress.current / progress.total) * 70) : 0
        }
        if (analysisPhase === 'generating') {
            return 70 + (cloudProgress.total > 0 ? Math.round((cloudProgress.current / cloudProgress.total) * 30) : 0)
        }
        return 0
    })()
    const unifiedMessage = analysisPhase === 'generating'
        ? (cloudProgress.message || '正在生成词云图...')
        : (progress.message || '正在分词...')

    return (
        <div className={styles.pageWrapper}>
            {contextHolder}
            <LoginModal
                open={loginModalOpen}
                onCancel={() => setLoginModalOpen(false)}
                continueAction={doAnalyze}
            />
            <WordcloudVipModal
                open={vipModalOpen}
                onClose={() => setVipModalOpen(false)}
                onSuccess={handleVipSuccess}
            />
            <div className={styles.layout}>

                {/* ── 左侧面板 30% ── */}
                <div className={styles.leftPanel}>

                    {/* 输入内容 */}
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHeader}>
                            <FileTextOutlined className={styles.sectionIcon} />
                            <span>输入内容</span>
                        </div>
                        <Tabs activeKey={inputMode} onChange={setInputMode} size="small" items={[
                            {
                                key: 'file',
                                label: <span><CloudUploadOutlined /> 上传文件</span>,
                                children: (
                                    <div className={styles.uploadArea}>
                                        <Dragger {...uploadProps} disabled={analyzing} className={styles.uploadDragger}>
                                            {fileList.length > 0 ? (
                                                <div className={styles.fileDisplay}>
                                                    <CloudUploadOutlined style={{ fontSize: 22, color: '#1677ff', flexShrink: 0 }} />
                                                    <span className={styles.fileName}>{fileList[0].name}</span>
                                                    <CloseCircleOutlined
                                                        className={styles.fileRemove}
                                                        onClick={(e) => { e.stopPropagation(); setFileList([]) }}
                                                        title="移除文件"
                                                    />
                                                </div>
                                            ) : (
                                                <div className={styles.uploadPrompt}>
                                                    <InboxOutlined style={{ color: '#1677ff', fontSize: 28 }} />
                                                    <p className={styles.uploadText}>点击或拖拽文件上传</p>
                                                    <p className={styles.uploadHint}>DOCX / TXT，不超过 10MB</p>
                                                </div>
                                            )}
                                        </Dragger>
                                    </div>
                                ),
                            },
                            {
                                key: 'text',
                                label: <span><FileTextOutlined /> 输入文本</span>,
                                children: (
                                    <TextArea className={styles.textArea} rows={4} placeholder="粘贴或输入文本（支持中英文）..." value={textContent} onChange={(e) => setTextContent(e.target.value)} disabled={analyzing} showCount maxLength={200000} />
                                ),
                            },
                        ]} />
                    </div>

                    {/* 样式设置 */}
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHeader}>
                            <SettingOutlined className={styles.sectionIcon} />
                            <span>样式设置</span>
                        </div>
                        <div className={styles.settingsItems}>
                            <div className={styles.settingItem} style={{ alignItems: 'flex-start' }}>
                                <span className={styles.settingItemLabel}>词云形状</span>
                                <div className={styles.shapeGrid}>
                                    {SHAPE_OPTIONS.map(s => (
                                        <button
                                            key={s.value}
                                            type="button"
                                            className={`${styles.shapeBtn} ${cloudShape === s.value ? styles.shapeBtnActive : ''}`}
                                            onClick={() => {
                                                setCloudShape(s.value)
                                                if (wordData.length > 0 && !analyzing && !generatingCloud) {
                                                    handleGenerateCloud(wordData, s.value, topN)
                                                }
                                            }}
                                            title={s.label}
                                        >
                                            <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                                                {s.svg}
                                            </svg>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 数据设置 */}
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionHeader}>
                            <FilterOutlined className={styles.sectionIcon} />
                            <span>数据设置</span>
                        </div>
                        <div className={styles.settingsItems}>
                            <div className={styles.settingItem}>
                                <span className={styles.settingItemLabel}>展示数量</span>
                                <InputNumber
                                    size="small"
                                    min={1}
                                    max={1000}
                                    value={topN}
                                    onChange={setTopN}
                                    placeholder="全部"
                                    style={{ width: 110 }}
                                    addonAfter="个词"
                                />
                                {wordData.length > 0 && (
                                    <span className={styles.settingHint}>
                                        {`共 ${wordData.length} 个词语`}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    <Button type="primary" size="large" className={styles.analyzeBtn} onClick={handleAnalyze} loading={analyzing} icon={<BarChartOutlined />} disabled={analyzing || generatingCloud}>
                        {analyzing ? '分析中...' : '开始分析'}
                    </Button>
                </div>

                {/* ── 右侧面板 70% ── */}
                <div className={styles.rightPanel}>
                    {!wordData.length && !analyzing && (
                        <div className={styles.emptyState}>
                            <CloudOutlined className={styles.emptyIcon} />
                            <p className={styles.emptyText}>上传文件或输入文本，点击「开始分析」生成词云</p>
                        </div>
                    )}

                    {analyzing && (
                        <div className={styles.progressRing}>
                            <div className={styles.progressRingTitle}>
                                {analysisPhase === 'generating' ? '生成词云图' : '分词分析'}
                            </div>
                            <Progress
                                type="circle"
                                percent={unifiedPercent}
                                size={76}
                                strokeWidth={7}
                                strokeColor={{ '0%': '#1a73e8', '100%': '#7c3aed' }}
                                trailColor="#eef3ff"
                                format={(p) => <span style={{ fontSize: 14, fontWeight: 700, color: '#1a73e8' }}>{p}%</span>}
                            />
                            <p className={styles.progressMessage}>{unifiedMessage}</p>
                        </div>
                    )}

                    {wordData.length > 0 && (
                        <div className={`${styles.sectionCard} ${styles.resultCard}`}>
                            <div className={styles.resultHeader}>
                                <Radio.Group value={chartTab} onChange={(e) => setChartTab(e.target.value)} buttonStyle="solid" size="small">
                                    <Radio.Button value="cloud"><CloudOutlined /> 词云图</Radio.Button>
                                    <Radio.Button value="bar"><BarChartOutlined /> 词频排行</Radio.Button>
                                    <Radio.Button value="table">📋 词语列表</Radio.Button>
                                </Radio.Group>
                                <div className={styles.downloadBtns}>
                                    <Button size="small" icon={<DownloadOutlined />} onClick={downloadWcImage}>词云图</Button>
                                    <Button size="small" icon={<DownloadOutlined />} onClick={downloadBarChart}>频率图</Button>
                                    <Button size="small" icon={<DownloadOutlined />} onClick={downloadCSV}>CSV</Button>
                                </div>
                            </div>

                            <div style={{ display: chartTab === 'cloud' ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                                {(generatingCloud && !analyzing) ? (
                                    <div className={styles.progressRing} style={{ position: 'relative', top: 'auto', left: 'auto', transform: 'none', margin: '40px auto' }}>
                                        <div className={styles.progressRingTitle}>生成词云</div>
                                        <Progress
                                            type="circle"
                                            percent={cloudProgress.total > 0 ? Math.round((cloudProgress.current / cloudProgress.total) * 100) : 0}
                                            size={76}
                                            strokeWidth={7}
                                            strokeColor={{ '0%': '#1a73e8', '100%': '#7c3aed' }}
                                            trailColor="#eef3ff"
                                            format={(p) => <span style={{ fontSize: 14, fontWeight: 700, color: '#1a73e8' }}>{p}%</span>}
                                        />
                                        <p className={styles.progressMessage}>{cloudProgress.message || '生成中...'}</p>
                                    </div>
                                ) : wcImageUrl ? (
                                    <div className={styles.cloudImageWrapper}>
                                        <img src={wcImageUrl} alt="词云图" className={styles.cloudImage} />
                                    </div>
                                ) : null}
                            </div>
                            <div style={{ display: chartTab === 'bar' ? 'block' : 'none' }}>
                                <div className={styles.barChartWrapper}>
                                    <div ref={barRef} className={styles.barChartContainer} />
                                </div>
                            </div>
                            {chartTab === 'table' && (
                                <div className={styles.tableWrapper}>
                                    <Table
                                        dataSource={tableData}
                                        columns={columns}
                                        rowKey={(_, idx) => idx}
                                        size="small"
                                        pagination={{ pageSize: 50, showTotal: (t) => `共 ${t} 条` }}
                                        scroll={{ y: 360 }}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

            </div>
        </div>
    )
}

export default WordCloudPage
